/**
 * preorder-properties.js  (Milk Street, 2026-10)
 *
 * Guarantees the "Pre-Order" and "Excluded Sitewide" line item properties are
 * present and CURRENT on every cart line before the shopper reaches checkout.
 *
 * Why this exists
 * ---------------
 * Historically the properties were attached only after an add, by the
 * `rebuy:cart.add` handler in rebuy-smart-cart.liquid
 * (add -> GET ?view=getProductTags -> POST /cart/change.js). Anything that
 * skipped or outran that chain (Rebuy Smart Search / Product Discovery /
 * upsell adds, fast checkout clicks, slow networks) produced orders with no
 * properties, and items that sat in a cart across a "Shipment:" tag change
 * carried a stale ship date into the order.
 *
 * What it does
 * ------------
 * 1. reconcile(): reads /cart.js (the source of truth for line keys and
 *    quantities), derives the expected properties for each line from the
 *    product's tags, and issues /cart/change.js for any line that is missing
 *    a property or whose Pre-Order text differs from the current tag.
 * 2. Runs reconcile() and WAITS for it before letting any checkout action
 *    proceed: the Rebuy Smart Cart "Checkout" and Shop Pay buttons, and the
 *    /cart page "Proceed to Checkout" form submit. A hard 3.5s ceiling means
 *    checkout is never blocked by a slow lookup; we fail open.
 * 3. Also runs opportunistically (non-blocking) shortly after a Rebuy add,
 *    as belt-and-braces for shoppers who never open the cart drawer.
 *
 * Product tags are read from the Rebuy cart object when available (it carries
 * product.tags for every line) and otherwise from the theme's existing
 * `?view=getProductTags` alternate template, cached per product for the session.
 *
 * Rules mirror rebuy-smart-cart.liquid and snippets/preorder-line-properties.liquid:
 *   tags include "pre-order" or "quiet pre-order" (case-insensitive) and a
 *   "Shipment: <text>" tag   ->  Pre-Order = "This item ships by <text>"
 *   tags include "Excluded Promo" ->  Excluded Sitewide = "This item is excluded from sitewide promotions."
 *
 * Debug: window.MSPreorderProps.reconcile().then(console.log)
 */
(function () {
  'use strict';

  var PRE_KEY = 'Pre-Order';
  var EXC_KEY = 'Excluded Sitewide';
  var EXC_VALUE = 'This item is excluded from sitewide promotions.';
  var CEILING_MS = 3500;
  var POST_ADD_DELAY_MS = 2500;
  var CACHE_KEY = 'ms_preorder_tags_v1';
  var BYPASS_ATTR = 'data-ms-preorder-reconciled';

  var tagCache = loadCache();
  var inFlight = null;

  // ---------- helpers ----------
  function loadCache() {
    try { return JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}'); } catch (e) { return {}; }
  }
  function saveCache() {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(tagCache)); } catch (e) { /* private mode etc. */ }
  }
  function log() {
    if (window.MS_PREORDER_DEBUG && window.console) console.log.apply(console, ['[preorder-props]'].concat([].slice.call(arguments)));
  }
  function normaliseTags(tags) {
    if (!tags) return [];
    if (Array.isArray(tags)) return tags.map(function (t) { return String(t).trim(); });
    return String(tags).split(',').map(function (t) { return t.trim(); }).filter(Boolean);
  }

  /** Expected properties for a product given its tags. */
  function expectedProps(tags) {
    var out = {};
    var lower = tags.map(function (t) { return t.toLowerCase(); });
    if (lower.indexOf('pre-order') !== -1 || lower.indexOf('quiet pre-order') !== -1) {
      for (var i = 0; i < tags.length; i++) {
        if (tags[i].indexOf('Shipment:') !== -1) {
          var ship = tags[i].split('Shipment:')[1].trim();
          if (ship) out[PRE_KEY] = 'This item ships by ' + ship;
          break;
        }
      }
    }
    if (tags.indexOf('Excluded Promo') !== -1) out[EXC_KEY] = EXC_VALUE;
    return out;
  }

  /** Tags from the Rebuy cart object, keyed by product_id, when Rebuy is up. */
  function rebuyTagsByProduct() {
    var map = {};
    try {
      var rc = window.Rebuy && window.Rebuy.Cart;
      var cart = rc && typeof rc.getCart === 'function' ? rc.getCart() : (rc && rc.cart);
      var items = cart && cart.items ? cart.items : [];
      items.forEach(function (it) {
        if (it && it.product && it.product.tags != null && it.product_id) {
          map[String(it.product_id)] = normaliseTags(it.product.tags);
        }
      });
    } catch (e) { log('rebuy tags unavailable', e); }
    return map;
  }

  function fetchTagsByHandle(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '?view=getProductTags', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : []; })
      .then(normaliseTags)
      .catch(function () { return null; });
  }

  function tagsForLine(line, rebuyMap) {
    var pid = String(line.product_id);
    if (rebuyMap[pid]) { tagCache[pid] = rebuyMap[pid]; return Promise.resolve(rebuyMap[pid]); }
    if (tagCache[pid]) return Promise.resolve(tagCache[pid]);
    return fetchTagsByHandle(line.handle).then(function (tags) {
      if (tags) { tagCache[pid] = tags; saveCache(); }
      return tags || [];
    });
  }

  function needsChange(line, expected) {
    var props = line.properties || {};
    var keys = Object.keys(expected);
    for (var i = 0; i < keys.length; i++) {
      if (props[keys[i]] !== expected[keys[i]]) return true;
    }
    return false;
  }

  function changeLine(line, expected) {
    var merged = Object.assign({}, line.properties || {}, expected);
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: line.key, quantity: line.quantity, properties: merged })
    }).then(function (r) { return r.ok; }).catch(function () { return false; });
  }

  // ---------- core ----------
  /**
   * Resolves to { checked, changed, errors }. Never rejects.
   * Concurrent callers share one run.
   */
  function reconcile() {
    if (inFlight) return inFlight;
    var result = { checked: 0, changed: 0, errors: 0 };
    inFlight = fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var rebuyMap = rebuyTagsByProduct();
        var lines = (cart && cart.items) || [];
        // Sequential so line keys stay valid between changes.
        return lines.reduce(function (chain, line) {
          return chain.then(function () {
            return tagsForLine(line, rebuyMap).then(function (tags) {
              result.checked++;
              var expected = expectedProps(tags);
              if (!Object.keys(expected).length || !needsChange(line, expected)) return;
              log('fixing line', line.key, expected);
              return changeLine(line, expected).then(function (ok) { ok ? result.changed++ : result.errors++; });
            });
          });
        }, Promise.resolve());
      })
      .then(function () {
        saveCache();
        if (result.changed && window.Rebuy && window.Rebuy.Cart && typeof window.Rebuy.Cart.fetchCart === 'function') {
          try { window.Rebuy.Cart.fetchCart(); } catch (e) { /* ignore */ }
        }
        log('done', result);
        return result;
      })
      .catch(function (e) { log('failed', e); result.errors++; return result; })
      .then(function (r) { inFlight = null; return r; });
    return inFlight;
  }

  /** reconcile() with a hard ceiling so checkout is never held hostage. */
  function reconcileBounded() {
    return Promise.race([
      reconcile(),
      new Promise(function (resolve) { setTimeout(function () { resolve({ timedOut: true }); }, CEILING_MS); })
    ]);
  }

  // ---------- checkout gates ----------
  // Rebuy Smart Cart "Checkout" + Shop Pay buttons, and the theme /cart page
  // "Proceed to Checkout" submit button (form="cart", name="checkout").
  // A capture-phase listener on document runs before the button's own handlers
  // (Vue click handler / native form submission), so it can hold the action back,
  // reconcile, then re-fire the click with a one-shot bypass attribute.
  var CHECKOUT_BUTTON_SELECTOR = [
    '.rebuy-cart__checkout-button',
    '.rebuy-cart__shop-pay-button',
    'button[name="checkout"]',
    'input[name="checkout"]',
    '#checkout'
  ].join(', ');

  document.addEventListener('click', function (evt) {
    var btn = evt.target && evt.target.closest ? evt.target.closest(CHECKOUT_BUTTON_SELECTOR) : null;
    if (!btn) return;
    if (btn.hasAttribute(BYPASS_ATTR)) { btn.removeAttribute(BYPASS_ATTR); return; } // our re-fired click
    evt.preventDefault();
    evt.stopImmediatePropagation();
    btn.setAttribute('aria-busy', 'true');
    reconcileBounded().then(function () {
      btn.removeAttribute('aria-busy');
      btn.setAttribute(BYPASS_ATTR, '1');
      btn.click();
    });
  }, true);

  // ---------- opportunistic passes ----------
  var postAddTimer = null;
  document.addEventListener('rebuy:cart.add', function () {
    clearTimeout(postAddTimer);
    postAddTimer = setTimeout(reconcile, POST_ADD_DELAY_MS);
  });

  // Expose for debugging / manual runs.
  window.MSPreorderProps = { reconcile: reconcile, expectedProps: expectedProps };
})();
